class NotesTabs {
  constructor(rootElement) {
    this.root = rootElement;
    this.tabButtons = Array.from(this.root.querySelectorAll('.notes-tabs__tab'));
    this.tabPanels = Array.from(this.root.querySelectorAll('.notes-tabs__panel'));

    if (this.tabButtons.length === 0 || this.tabPanels.length === 0) {
      return;
    }

    this.activeIndex = this.getDefaultActiveIndex();

    this.bindEvents();
    this.handleUrlHash();
    this.setupCopyDeterrent();
  }

  getDefaultActiveIndex() {
    const activeIndex = this.tabButtons.findIndex((button) => button.classList.contains('notes-tabs__tab--active'));
    return activeIndex > -1 ? activeIndex : 0;
  }

  bindEvents() {
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.switchTab(index));
      button.addEventListener('keydown', (event) => this.handleKeydown(event, index));
    });

    // Listen for hash changes (deep linking)
    window.addEventListener('hashchange', () => this.handleUrlHash());
  }

  switchTab(index) {
    if (index === this.activeIndex) return;

    // Update active tab button
    const previousTab = this.tabButtons[this.activeIndex];
    previousTab.classList.remove('notes-tabs__tab--active');
    previousTab.setAttribute('aria-selected', 'false');
    previousTab.setAttribute('tabindex', '-1');

    const nextTab = this.tabButtons[index];
    nextTab.classList.add('notes-tabs__tab--active');
    nextTab.setAttribute('aria-selected', 'true');
    nextTab.setAttribute('tabindex', '0');
    nextTab.focus();

    // Update active panel
    const previousPanel = this.tabPanels[this.activeIndex];
    previousPanel.classList.remove('notes-tabs__panel--active');
    previousPanel.setAttribute('hidden', '');
    previousPanel.setAttribute('aria-hidden', 'true');

    const nextPanel = this.tabPanels[index];
    nextPanel.classList.add('notes-tabs__panel--active');
    nextPanel.removeAttribute('hidden');
    nextPanel.setAttribute('aria-hidden', 'false');

    this.activeIndex = index;

    // Update URL hash for deep linking
    const slug = this.getTabSlug(index);
    if (slug) {
      history.replaceState(null, null, `#${slug}`);
    }
  }

  handleKeydown(event, index) {
    let newIndex = index;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = index > 0 ? index - 1 : this.tabButtons.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = index < this.tabButtons.length - 1 ? index + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabButtons.length - 1;
        break;
      default:
        return;
    }

    this.switchTab(newIndex);
  }

  handleUrlHash() {
    const hash = window.location.hash.substring(1);
    if (!hash) return;

    const tabIndex = this.findTabBySlug(hash);
    if (tabIndex !== -1 && tabIndex !== this.activeIndex) {
      this.switchTab(tabIndex);
    }
  }

  getTabSlug(index) {
    const tabElement = this.tabButtons[index];
    if (!tabElement) return null;

    // Check if there's a data-slug attribute (from Liquid block settings)
    const dataSlug = tabElement.getAttribute('data-slug');
    if (dataSlug) return dataSlug;

    // Fallback to generating slug from tab text
    const tabText = tabElement.textContent.trim().toLowerCase();
    return tabText.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  findTabBySlug(slug) {
    return this.tabButtons.findIndex((button, index) => this.getTabSlug(index) === slug);
  }

  setupCopyDeterrent() {
    // Light deterrent: Show a notice when copy events are triggered
    const contentElements = this.root.querySelectorAll('.notes-tabs__content');

    contentElements.forEach(element => {
      element.addEventListener('copy', (event) => {
        event.preventDefault();

        // Show a subtle notice
        this.showCopyNotice();

        // Allow the copy but show the deterrent message
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const clonedContents = range.cloneContents();

          const tempContainer = document.createElement('div');
          tempContainer.appendChild(clonedContents);

          const copiedHtml = tempContainer.innerHTML;
          const copiedText = selection.toString();
          const watermarkHtml = '<p style="color: #666; font-style: italic;">Content copied from internal notes - please respect confidentiality.</p>';
          const watermarkText = '\n\n[Content copied from internal notes - please respect confidentiality]';

          event.clipboardData.setData('text/html', `${copiedHtml}${watermarkHtml}`);
          event.clipboardData.setData('text/plain', `${copiedText}${watermarkText}`);
        }
      });

      // Prevent context menu (right-click)
      element.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        this.showCopyNotice();
      });

      // Prevent drag selection
      element.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });
    });
  }

  showCopyNotice() {
    // Remove any existing notice
    const existingNotice = document.querySelector('.notes-copy-notice');
    if (existingNotice) {
      existingNotice.remove();
    }

    // Create and show notice
    const notice = document.createElement('div');
    notice.className = 'notes-copy-notice';
    notice.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #333;
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        font-size: 14px;
        z-index: 9999;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        max-width: 300px;
      ">
        This content is for internal use only. Please respect confidentiality.
        <button style="
          background: none;
          border: none;
          color: white;
          float: right;
          margin-left: 10px;
          cursor: pointer;
          font-size: 16px;
        " onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;

    document.body.appendChild(notice);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notice.parentElement) {
        notice.remove();
      }
    }, 5000);
  }
}

// Initialize when DOM is ready
const initNotesTabs = () => {
  document.querySelectorAll('.notes-tabs').forEach((tabsElement) => {
    new NotesTabs(tabsElement);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNotesTabs);
} else {
  initNotesTabs();
}
